import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { AlertTriangle, CheckCircle2, Film, ImageIcon, Loader2, RotateCcw, X, XCircle } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { Dropzone, type PickedFile } from '@components/Dropzone';
import { ProgressBar } from '@components/ProgressBar';
import { guessContentType, isUploadCancelled, uploadMediaFile } from '@services/mediaUpload';
import {
  IGNORE_REASON_LABELS,
  partitionPicked,
  selectionSummaryText,
  unreadableAsIgnored,
  type IgnoredFile,
} from '@services/mediaFolderUtils';
import { formatBytes } from '@/utils/formatters';

/** Quantos arquivos sobem ao mesmo tempo. */
const CONCURRENCY = 3;
/** Linhas concluídas/canceladas mantidas na tela (as mais recentes). */
const RECENT_ROWS = 50;
/** Próximos da fila mostrados. */
const NEXT_QUEUED_ROWS = 5;
/** Linhas com erro mostradas (o resto aparece só no contador). */
const ERROR_ROWS = 100;
/** Itens ignorados listados no "ver lista". */
const IGNORED_ROWS = 500;
/** Intervalo de atualização da barra de progresso (ms) — evita re-render a cada evento do XHR. */
const PROGRESS_TICK_MS = 300;

type ItemStatus = 'queued' | 'uploading' | 'done' | 'error' | 'cancelled';

interface UploadItem {
  key: number;
  file: File;
  /** Caminho dentro da pasta ('' quando o arquivo veio solto). */
  relativePath: string;
  status: ItemStatus;
  error?: string;
}

interface MediaUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Chamado quando a fila esvazia com ao menos um envio concluído (para recarregar a lista). */
  onUploaded: () => void;
}

const errorMessage = (err: unknown): string => (err instanceof Error ? err.message : 'Falha no envio.');

const displayPath = (it: UploadItem) => it.relativePath || it.file.name;

/**
 * Envio de arquivos ou pastas inteiras (com subpastas) direto ao Blob (ver mediaUpload.ts):
 * 3 por vez, progresso agregado, só as linhas relevantes na tela (a fila pode ter milhares),
 * "Tentar de novo" para os que falharam e aviso ao fechar a aba durante o envio.
 */
export const MediaUploadDialog: React.FC<MediaUploadDialogProps> = ({ isOpen, onClose, onUploaded }) => {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [ignored, setIgnored] = useState<IgnoredFile[]>([]);
  const [, setTick] = useState(0);
  const nextKey = useRef(1);
  const controllers = useRef(new Map<number, AbortController>());
  /** Bytes enviados de quem está subindo agora (fora do estado para não re-renderizar a cada evento). */
  const progress = useRef(new Map<number, number>());
  const notifiedDone = useRef(0);

  const patch = useCallback((key: number, changes: Partial<UploadItem>) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...changes } : it)));
  }, []);

  const start = useCallback(
    async (item: UploadItem) => {
      const controller = new AbortController();
      controllers.current.set(item.key, controller);
      progress.current.set(item.key, 0);
      try {
        await uploadMediaFile(item.file, {
          signal: controller.signal,
          relativePath: item.relativePath,
          onProgress: ({ loaded }) => progress.current.set(item.key, loaded),
        });
        patch(item.key, { status: 'done', error: undefined });
      } catch (err) {
        if (isUploadCancelled(err)) patch(item.key, { status: 'cancelled' });
        else patch(item.key, { status: 'error', error: errorMessage(err) });
      } finally {
        controllers.current.delete(item.key);
        progress.current.delete(item.key);
      }
    },
    [patch],
  );

  // Fila: mantém até CONCURRENCY envios em andamento.
  useEffect(() => {
    let running = 0;
    const toStart: UploadItem[] = [];
    for (const it of items) {
      if (it.status === 'uploading') running++;
    }
    const free = CONCURRENCY - running;
    if (free <= 0) return;
    for (const it of items) {
      if (toStart.length >= free) break;
      if (it.status === 'queued') toStart.push(it);
    }
    if (toStart.length === 0) return;
    const starting = new Set(toStart.map((s) => s.key));
    setItems((prev) => prev.map((it) => (starting.has(it.key) ? { ...it, status: 'uploading' as const } : it)));
    toStart.forEach((it) => start(it));
  }, [items, start]);

  const stats = useMemo(() => {
    const s = { queued: 0, uploading: 0, done: 0, error: 0, cancelled: 0, totalBytes: 0, doneBytes: 0 };
    for (const it of items) {
      s[it.status]++;
      if (it.status !== 'cancelled') s.totalBytes += it.file.size;
      if (it.status === 'done') s.doneBytes += it.file.size;
    }
    return s;
  }, [items]);

  const busy = stats.queued + stats.uploading > 0;

  // Enquanto envia, re-render periódico para a barra de progresso.
  useEffect(() => {
    if (!busy) return;
    const t = window.setInterval(() => setTick((n) => n + 1), PROGRESS_TICK_MS);
    return () => window.clearInterval(t);
  }, [busy]);

  // Fechar/recarregar a aba no meio do envio perde o que falta: o navegador pergunta antes.
  useEffect(() => {
    if (!busy) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'Há envios em andamento. Se fechar esta aba, os arquivos que faltam não serão enviados.';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [busy]);

  // Fila esvaziou com novos concluídos → a tela recarrega.
  useEffect(() => {
    if (!busy && stats.done > notifiedDone.current) {
      notifiedDone.current = stats.done;
      onUploaded();
    }
  }, [busy, stats.done, onUploaded]);

  const addFiles = (picked: PickedFile[], unreadable: string[]) => {
    const { accepted, ignored: skipped } = partitionPicked(picked);
    const allSkipped = [...skipped, ...unreadableAsIgnored(unreadable)];
    if (allSkipped.length > 0) setIgnored((prev) => [...prev, ...allSkipped]);
    if (accepted.length === 0) return;
    const fresh: UploadItem[] = accepted.map((p) => ({
      key: nextKey.current++,
      file: p.file,
      relativePath: p.relativePath,
      status: 'queued',
    }));
    setItems((prev) => [...prev, ...fresh]);
  };

  const cancel = (item: UploadItem) => {
    if (item.status === 'queued') patch(item.key, { status: 'cancelled' });
    else controllers.current.get(item.key)?.abort();
  };

  const cancelAll = () => {
    setItems((prev) => prev.map((it) => (it.status === 'queued' ? { ...it, status: 'cancelled' as const } : it)));
    controllers.current.forEach((c) => c.abort());
  };

  const retryFailed = () => {
    setItems((prev) =>
      prev.map((it) => (it.status === 'error' ? { ...it, status: 'queued' as const, error: undefined } : it)),
    );
  };

  const handleClose = () => {
    if (busy) return;
    setItems([]);
    setIgnored([]);
    notifiedDone.current = 0;
    onClose();
  };

  // Só o que importa na tela: enviando, com erro, os próximos da fila e os últimos concluídos.
  const view = useMemo(() => {
    const uploading: UploadItem[] = [];
    const errors: UploadItem[] = [];
    const nextQueued: UploadItem[] = [];
    const finished: UploadItem[] = [];
    for (const it of items) {
      if (it.status === 'uploading') uploading.push(it);
      else if (it.status === 'error') errors.push(it);
      else if (it.status === 'queued') {
        if (nextQueued.length < NEXT_QUEUED_ROWS) nextQueued.push(it);
      } else finished.push(it);
    }
    return {
      rows: [...uploading, ...errors.slice(0, ERROR_ROWS), ...nextQueued, ...finished.slice(-RECENT_ROWS).reverse()],
      hiddenErrors: Math.max(0, errors.length - ERROR_ROWS),
      hiddenFinished: Math.max(0, finished.length - RECENT_ROWS),
    };
  }, [items]);

  let inFlightBytes = 0;
  progress.current.forEach((v) => (inFlightBytes += v));
  const loadedBytes = stats.doneBytes + inFlightBytes;
  const finishedCount = stats.done + stats.error + stats.cancelled;
  const overallPct = stats.totalBytes > 0 ? (loadedBytes / stats.totalBytes) * 100 : 0;
  const hiddenQueued = Math.max(0, stats.queued - NEXT_QUEUED_ROWS);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" closeOnBackdrop={!busy} showCloseButton={!busy}>
      <div className="px-6 py-4 border-b border-slate-200">
        <h2 className="text-h3">Enviar arquivos</h2>
        <p className="text-small mt-1">
          Os arquivos vão direto para o armazenamento; depois a IA descreve, gera as tags e procura duplicadas.
        </p>
      </div>

      <div className="px-6 py-4 space-y-4 max-h-[65vh] overflow-y-auto custom-scrollbar">
        <Dropzone
          onFiles={addFiles}
          allowFolders
          hint={
            <div className="space-y-1 max-w-xl">
              <p>
                Vários arquivos ou uma pasta inteira (com subpastas). JPG, PNG, WEBP, HEIC, GIF, TIFF e vídeos MP4, MOV,
                M4V, WEBM, AVI, MKV — até 2 GB cada. Outros tipos e arquivos de sistema são ignorados.
              </p>
              <p className="text-emerald-800">
                O nome e a estrutura das pastas são enviados à IA como contexto: use nomes descritivos, como “CASACOR
                2025” ou “Caixas de Couro Savana”.
              </p>
            </div>
          }
        />

        {(items.length > 0 || ignored.length > 0) && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 space-y-1">
            <p>{selectionSummaryText(items.length, ignored)}</p>
            {ignored.length > 0 && (
              <details className="text-xs text-slate-600">
                <summary className="cursor-pointer text-emerald-700 hover:underline select-none">
                  Ver arquivos ignorados
                </summary>
                <ul className="mt-1 max-h-40 overflow-y-auto custom-scrollbar space-y-0.5">
                  {ignored.slice(0, IGNORED_ROWS).map((ig, i) => (
                    <li key={`${ig.path}-${i}`} className="flex gap-2">
                      <span className="truncate" title={ig.path}>
                        {ig.path}
                      </span>
                      <span className="text-slate-400 whitespace-nowrap">— {IGNORE_REASON_LABELS[ig.reason]}</span>
                    </li>
                  ))}
                  {ignored.length > IGNORED_ROWS && (
                    <li className="text-slate-400">
                      … e mais {(ignored.length - IGNORED_ROWS).toLocaleString('pt-BR')}
                    </li>
                  )}
                </ul>
              </details>
            )}
          </div>
        )}

        {items.length > 0 && (
          <div className="space-y-1">
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-700">
              <span className="tabular-nums">
                {finishedCount.toLocaleString('pt-BR')} de {items.length.toLocaleString('pt-BR')} arquivos
              </span>
              <span className="text-slate-500 tabular-nums">
                {formatBytes(loadedBytes)} / {formatBytes(stats.totalBytes)}
              </span>
            </div>
            <ProgressBar value={overallPct} variant={stats.error > 0 && !busy ? 'error' : 'default'} />
            {busy && (
              <p className="flex items-center gap-1.5 text-xs text-amber-700">
                <AlertTriangle size={14} /> Não feche esta aba até o envio terminar.
              </p>
            )}
          </div>
        )}

        {view.rows.length > 0 && (
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
            {view.rows.map((it) => {
              const loaded = it.status === 'uploading' ? progress.current.get(it.key) ?? 0 : 0;
              const pct = it.file.size > 0 ? (loaded / it.file.size) * 100 : 0;
              const Icon = guessContentType(it.file.name, it.file.type).startsWith('video/') ? Film : ImageIcon;
              const path = displayPath(it);
              return (
                <li key={it.key} className="px-3 py-2 flex items-center gap-3">
                  <Icon size={18} className="text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="flex min-w-0 text-sm" title={path}>
                        {path.includes('/') && (
                          <span className="truncate text-slate-400">{path.slice(0, path.lastIndexOf('/') + 1)}</span>
                        )}
                        <span className="truncate flex-shrink-0 max-w-[70%] text-slate-800">{it.file.name}</span>
                      </p>
                      <span className="text-xs text-slate-500 whitespace-nowrap tabular-nums">
                        {it.status === 'uploading'
                          ? `${formatBytes(loaded)} de ${formatBytes(it.file.size)}`
                          : formatBytes(it.file.size)}
                      </span>
                    </div>
                    {it.status === 'uploading' && <ProgressBar value={pct} className="mt-1" />}
                    {it.status === 'error' && <p className="text-xs text-red-600 mt-0.5">{it.error}</p>}
                    {it.status === 'cancelled' && <p className="text-xs text-slate-400 mt-0.5">Cancelado</p>}
                    {it.status === 'queued' && <p className="text-xs text-slate-400 mt-0.5">Na fila</p>}
                  </div>
                  <div className="w-6 flex justify-center">
                    {(it.status === 'queued' || it.status === 'uploading') && (
                      <button
                        type="button"
                        onClick={() => cancel(it)}
                        className="p-1 text-slate-400 hover:text-red-600 rounded"
                        title="Cancelar"
                        aria-label={`Cancelar ${it.file.name}`}
                      >
                        <X size={16} />
                      </button>
                    )}
                    {it.status === 'done' && <CheckCircle2 size={18} className="text-emerald-600" />}
                    {it.status === 'error' && <XCircle size={18} className="text-red-500" />}
                  </div>
                </li>
              );
            })}
            {(hiddenQueued > 0 || view.hiddenErrors > 0 || view.hiddenFinished > 0) && (
              <li className="px-3 py-2 text-xs text-slate-500 bg-slate-50">
                {[
                  hiddenQueued > 0 && `mais ${hiddenQueued.toLocaleString('pt-BR')} na fila`,
                  view.hiddenErrors > 0 && `mais ${view.hiddenErrors.toLocaleString('pt-BR')} com erro`,
                  view.hiddenFinished > 0 && `${view.hiddenFinished.toLocaleString('pt-BR')} concluídos anteriores não listados`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </li>
            )}
          </ul>
        )}
      </div>

      <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-lg flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-600 flex flex-wrap items-center gap-3">
          {items.length === 0 ? (
            <span>Nenhum arquivo escolhido.</span>
          ) : (
            <>
              {busy && <Loader2 size={14} className="animate-spin text-emerald-600" />}
              <span className={clsx(stats.done > 0 && 'text-emerald-700 font-medium')}>
                {stats.done.toLocaleString('pt-BR')} enviado(s)
              </span>
              {stats.queued > 0 && <span className="text-slate-500">{stats.queued.toLocaleString('pt-BR')} na fila</span>}
              {stats.error > 0 && <span className="text-red-600">{stats.error.toLocaleString('pt-BR')} com erro</span>}
              {stats.cancelled > 0 && (
                <span className="text-slate-400">{stats.cancelled.toLocaleString('pt-BR')} cancelado(s)</span>
              )}
            </>
          )}
        </div>
        <div className="flex gap-2">
          {stats.error > 0 && (
            <Button variant="outline" size="sm" icon={<RotateCcw />} onClick={retryFailed}>
              Tentar de novo os que falharam
            </Button>
          )}
          {busy && (
            <Button variant="outline" size="sm" onClick={cancelAll}>
              Cancelar todos
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={handleClose} disabled={busy}>
            {busy ? 'Enviando…' : 'Fechar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MediaUploadDialog;
